import React, {useEffect, useMemo, useState} from 'react';
import {DataPoint, GroupedData, View} from '../../App';
import {useNavigate, useParams} from 'react-router-dom';
import MapChart from '../../components/MapChart';
import Drawer from '../../components/Drawer';
import Footer from '../../components/Footer';
import HeaderPanel from '../../components/HeaderPanel';
import ReactTooltip from 'react-tooltip';
import {MapLegend} from '../../components/MapLegend/MapLegend';
export interface MapPageProps {
  data: DataPoint[];
  availableCountries: Map<string, boolean>;
}

const MapPage = ({data, availableCountries}: MapPageProps) => {
  const params = useParams();
  const navigate = useNavigate();

  const [hideMap, setHideMap] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const {id} = params;
  const [filteredCity, setFilteredCity] = useState<string>();
  const [filterProductionStatus, setFilterProductionStatus] = useState<
    string[]
  >([]);
  const [filteredCountry, setFilteredCountry] = useState<string[]>([]);
  const [filteredInvestorCountry, setFilteredInvestorCountry] = useState<
    string[]
  >([]);
  const [filteredCategory, setFilteredCategory] = useState<string[]>([]);
  const [hoveredCity, setHoveredCity] = useState<string>();
  const [tooltipContent, setTooltipContent] = useState<React.ReactNode | null>(
      null,
  );

  const hasFilters =
    filteredCountry.length > 0 ||
    filteredInvestorCountry.length > 0 ||
    filteredCategory.length > 0 ||
    filterProductionStatus.length > 0;

  useEffect(() => {
    setFilteredCity(null);
  }, [searchValue]);

  const filteredData = useMemo(() => {
    return data.filter((dataPoint) => {
      let isFiltered = true;

      if (
        filteredCountry.length > 0 &&
        !filteredCountry.includes(dataPoint.Country)
      ) {
        isFiltered = false;
      }

      if (
        filteredInvestorCountry.length > 0 &&
        !filteredInvestorCountry.includes(dataPoint.Investor_country)
      ) {
        isFiltered = false;
      }

      if (
        filteredCategory.length > 0 &&
        !filteredCategory.includes(dataPoint.Category)
      ) {
        isFiltered = false;
      }

      if (
        filterProductionStatus.length > 0 &&
        !filterProductionStatus.includes(
            dataPoint.Production_status.toLowerCase(),
        )
      ) {
        isFiltered = false;
      }

      return isFiltered;
    });
  }, [
    data,
    filteredCountry,
    filteredInvestorCountry,
    filteredCategory,
    filterProductionStatus,
  ]);

  const groupedData = useMemo(() => {
    const groupedData: GroupedData = {};

    data.forEach((dataPoint) => {
      const city = dataPoint.Location;
      const entity = dataPoint.Entity;
      if (!groupedData[city]) {
        groupedData[city] = {
          country: dataPoint.Country,
          entities: {},
        };
      }
      if (!groupedData[city].entities[entity]) {
        groupedData[city].entities[entity] = [];
      }
      groupedData[city].entities[entity].push(dataPoint);
    });

    return groupedData;
  }, [data]);

  const goBack = () => {
    if (filteredCity) {
      setFilteredCity(null);
    }
    if (id) {
      navigate('/map');
    } else if (hasFilters) {
      setFilteredCountry([]);
      setFilteredInvestorCountry([]);
      setFilteredCategory([]);
      setFilterProductionStatus([]);
    }
    navigate('/map');
  };

  const setId = (id: string) => {
    navigate(`/map/${id}`);
  };

  const setCountry = (country: string) => {
    setFilteredCountry([country]);
  };

  return (
    <div className="mappage">
      <HeaderPanel
        setSearchValue={setSearchValue}
        setId={setId}
        setFilteredCity={setFilteredCity}
        searchValue={searchValue}
        setHoveredCity={setHoveredCity}
        filterProductionStatus={filterProductionStatus}
        setFilterProductionStatus={setFilterProductionStatus}
        filteredCountry={filteredCountry}
        setFilteredCountry={setFilteredCountry}
        filteredInvestorCountry={filteredInvestorCountry}
        setFilteredInvestorCountry={setFilteredInvestorCountry}
        filteredCategory={filteredCategory}
        setFilteredCategory={setFilteredCategory}
        data={data}
        hideMap={hideMap}
        setHideMap={setHideMap}
      />
      <MapChart
        setId={setId}
        countries={filteredCountry}
        setCountry={setCountry}
        data={filteredData}
        filteredCity={filteredCity}
        hoveredCity={hoveredCity}
        hasDrawer={hasFilters || !!id}
        setFilteredCity={setFilteredCity}
        setTooltipContent={setTooltipContent}
        availableCountries={availableCountries}
      />
      {tooltipContent && (
        <ReactTooltip id="marker-tooltip" padding="10px">
          {tooltipContent}
        </ReactTooltip>
      )}
      <Drawer
        hideMap={hideMap}
        setHideMap={setHideMap}
        id={id}
        setId={setId}
        groupedData={groupedData}
        data={data}
        filteredData={filteredData}
        filteredCity={filteredCity}
        hasFilters={hasFilters}
        searchValue={searchValue}
        goBack={goBack}
      />
      <Footer />
      <MapLegend />
    </div>
  );
};

export default MapPage;
